import type { UserPreview, SmsFilter } from "@shared/sms-filters";
import { TanstackError } from "../client";

export type { UserPreview, SmsFilter };

export interface PreviewResponse {
  users: UserPreview[];
}

export interface BulkSmsRecipientOk {
  userId: string;
  email: string;
  label: string;
}

export interface BulkSmsRecipientFailed {
  userId: string;
  email: string;
  label: string;
  error: string;
}

export interface SendResponse {
  success: boolean;
  sent: number;
  failed: number;
  errors: string[];
  succeeded: BulkSmsRecipientOk[];
  failedRecipients: BulkSmsRecipientFailed[];
}

interface SendApiResponse extends SendResponse {
  error?: string;
}

export async function getConsentedUsers(): Promise<PreviewResponse> {
  const response = await fetch(
    `/api/internal/sms-notifications/preview`,
    { credentials: "include" },
  );

  const result: PreviewResponse = await response.json();

  if (!response.ok) {
    throw new TanstackError(response.status, "Failed to fetch preview");
  }

  return result;
}

// Heroku's router kills requests after 30s, but the server keeps sending.
// Keep each batch small enough to respond well within that limit
// (~250ms pacing + send latency per recipient).
const BULK_SMS_CHUNK_SIZE = 10;

function recipientLabel(user: UserPreview): string {
  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || "Unknown";
  return `${name} (${user.email})`;
}

async function sendBulkSmsChunk(
  message: string,
  userIds: string[],
  confirmationPassword: string,
): Promise<SendResponse> {
  const response = await fetch("/api/internal/sms-notifications/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message, userIds, confirmationPassword }),
  });

  let result: SendApiResponse;
  try {
    result = await response.json();
  } catch {
    throw new TanstackError(
      response.status,
      `Server returned an unexpected non-JSON response (status ${response.status})`,
    );
  }

  if (!response.ok || !result.success) {
    throw new TanstackError(
      response.status,
      result.error || "Failed to send SMS",
    );
  }

  return result;
}

export async function sendBulkSms(
  message: string,
  users: UserPreview[],
  confirmationPassword: string,
): Promise<SendResponse> {
  const chunks: UserPreview[][] = [];
  for (let i = 0; i < users.length; i += BULK_SMS_CHUNK_SIZE) {
    chunks.push(users.slice(i, i + BULK_SMS_CHUNK_SIZE));
  }

  const aggregate: SendResponse = {
    success: true,
    sent: 0,
    failed: 0,
    errors: [],
    succeeded: [],
    failedRecipients: [],
  };

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    try {
      const result = await sendBulkSmsChunk(
        message,
        chunk.map((u) => u.userId),
        confirmationPassword,
      );
      aggregate.sent += result.sent;
      aggregate.failed += result.failed;
      aggregate.errors.push(...result.errors);
      aggregate.succeeded.push(...result.succeeded);
      aggregate.failedRecipients.push(...result.failedRecipients);
    } catch (error) {
      // Nothing sent yet: surface the error directly (e.g. bad password).
      if (i === 0) throw error;

      // Later batch failed: don't lose results from batches already sent.
      // Mark this batch's recipients as failed and keep going.
      const errMsg =
        error instanceof Error ? error.message : "Failed to send SMS";
      for (const user of chunk) {
        aggregate.failed++;
        aggregate.errors.push(`${user.email}: ${errMsg}`);
        aggregate.failedRecipients.push({
          userId: user.userId,
          email: user.email,
          label: recipientLabel(user),
          error: errMsg,
        });
      }
    }
  }

  return aggregate;
}
